import React, { useEffect, useState } from 'react';
import * as d3 from 'd3';
import AppleData from '../apple.json';
import IntelData from '../intel.json';
import {initializeSvg, ChartMargin } from './utility';

const svgId = "chart-apple-intel";
const defaultAnnotation = `<div class="annotation-container"><h5>Click on a bar to view more information.</h5></div>`
const opts = {
    ChartWidth: 1024,
    ChartHeight: 640
};

interface DataElement {
    CPU: string;
    SINGLE_CORE: number;
    MULTI_CORE: number;
    CORES: number;
    CLOCK: number;
    YEAR: number;
    TDP: number;
}


const ScaleX = d3.scaleBand()
    .domain(['2013', '2014', '2015', '2017', '2018', '2020'])
    .rangeRound([0, opts.ChartWidth - ChartMargin.left]);

const AxisX = d3.axisBottom(ScaleX)
    .ticks(7)
    .tickSizeOuter(0);

export default function AppleIntel() {
    const [annotation, setAnnotation] = useState('');
    const [currData, setCurrData] = useState(null);

    // Y-Axis will be geekbench Single-core scores
    const ScaleYSingle = d3.scaleLinear()
        .domain([0, 1200])
        .range([opts.ChartHeight, 0]);


    function mouseOverGray(d: any, i: any, e: any) {
        e[i].setAttribute('fill', 'gray');
    }

    function mouseOverNavy(d: any, i: any, e: any) {
        e[i].setAttribute('fill', 'navy');
    }

    function mouseOutSilver(d: any, i: any, e: any) {
        e[i].setAttribute('fill', 'silver');
    }

    function mouseOutBlue(d: any, i: any, e: any) {
        e[i].setAttribute('fill', 'blue');
    }

    function clickHandlerSingle(d: any, i: any, e: any) {
        e[i].setAttribute('fill', 'orange');

        const html = `
        <div class="annotation-container">
            <h5>${d.CPU}</h5>
            <li>
                Cores: <strong>${d.CORES}</strong>
            </li>
            <li>
                Released: <strong>${d.YEAR}</strong>
            </li>
            <li>
                TDP: <strong>${d.TDP}</strong> W
            </li>
            <li>
                Score: <strong>${d.SINGLE_CORE}</strong>
            </li>
        </div>
                        `;
        setAnnotation(html);
    }

    function clickHandlerMulti(d: any, i: any, e: any) {
        e[i].setAttribute('fill', 'orange');
        const html = `
        <div class="annotation-container">
            <h5>${d.CPU}</h5>
            <li>
                Cores: <strong>${d.CORES}</strong>
            </li>
            <li>
                Released: <strong>${d.YEAR}</strong>
            </li>
            <li>
                TDP: <strong>${d.TDP}</strong> W
            </li>
            <li>
                Score: <strong>${d.MULTI_CORE}</strong>
            </li>
        </div>
                        `;
        setAnnotation(html);
    }

    function drawSingleChart() {
        setAnnotation(defaultAnnotation);
        d3.selectAll(`svg[id=${svgId}] > *`).remove();

        const AxisY = d3.axisLeft(ScaleYSingle)
            .ticks(5)
            .tickFormat(d3.format('d'));

        d3.select(`svg[id=${svgId}]`)
            .append('g')
            .selectAll('rect')
            .data(AppleData)
            .enter()
            .append('rect')
            .on('mouseover', mouseOverGray)
            .on('mouseout', mouseOutSilver)
            .on("click", clickHandlerSingle)
            .attr("class", "bar-element")
            .attr("fill", 'silver')
            .attr("width", ScaleX.bandwidth() / 4)
            .attr("height", (d, i, c) => opts.ChartHeight - ScaleYSingle(d.SINGLE_CORE))
            .attr("x", (d, i, c) => {
                const year = ScaleX((d as any).YEAR.toString()) || '';
                return year;
            })
            .attr("height", 0)
            .attr("y", opts.ChartHeight)
            .attr("transform", (d) => `translate(${ChartMargin.left * 1.75}, ${-ChartMargin.bottom - 3})`)
            .transition()
            .duration(1000)
            .attr("height", (d, i, c) => opts.ChartHeight - ScaleYSingle(d.SINGLE_CORE))
            .attr("y", d => ScaleYSingle(d.SINGLE_CORE));

        d3.select(`svg[id=${svgId}]`)
            .append('g')
            .selectAll('rect')
            .data(IntelData)
            .enter()
            .append('rect')
            .on('mouseover', mouseOverNavy)
            .on('mouseout', mouseOutBlue)
            .on("click", clickHandlerSingle)
            .attr("class", "bar-element")
            .attr("fill", 'blue')
            .attr("width", ScaleX.bandwidth() / 4)
            .attr("height", (d, i, c) => opts.ChartHeight - ScaleYSingle(d.SINGLE_CORE))
            .attr("x", (d, i, c) => {
                const year = ScaleX((d as any).YEAR.toString()) || '';
                return year;
            })
            .attr("height", 0)
            .attr("y", opts.ChartHeight)
            .attr("transform", (d) => `translate(${ChartMargin.left * 2.5}, ${-ChartMargin.bottom - 3})`)
            .transition()
            .duration(1000)
            .attr("height", (d, i, c) => opts.ChartHeight - ScaleYSingle(d.SINGLE_CORE))
            .attr("y", d => ScaleYSingle(d.SINGLE_CORE));

    d3.select(`svg[id=${svgId}]`)
        .append('g')
        .attr('transform', `translate(${ChartMargin.left}, ${ChartMargin.bottom})`)
        .call(AxisY);

    d3.select(`svg[id=${svgId}]`)
        .append('g')
        .attr('transform', `translate(${ChartMargin.left - 4}, ${opts.ChartHeight - 3 - ChartMargin.bottom})`)
        .call(AxisX);
    }
    
    function drawMultiChart() {
        setAnnotation(defaultAnnotation);
        d3.selectAll(`svg[id=${svgId}] > *`).remove();

        // Y-Axis will be geekbench multi-core scores
        const ScaleY = d3.scaleLinear()
            .domain([0, 5000])
            .range([opts.ChartHeight, 0]);

        const AxisY = d3.axisLeft(ScaleY)
            .ticks(5)
            .tickFormat(d3.format('d'));
        
        d3.select(`svg[id=${svgId}]`)
            .append('g')
            .selectAll('rect')
            .data(AppleData)
            .enter()
            .append('rect')
            .on('mouseover', mouseOverGray)
            .on('mouseout', mouseOutSilver)
            .on("click", clickHandlerMulti)
            .attr("class", "bar-element")
            .attr("fill", 'silver')
            .attr("width", ScaleX.bandwidth() / 4)
            .attr("height", 0)
            .attr("y", opts.ChartHeight)
            .attr("x", (d, i, c) => {
                const year = ScaleX((d as any).YEAR.toString()) || '';
                return year;
            })
            .attr("transform", (d) => `translate(${ChartMargin.left * 1.75}, ${-ChartMargin.bottom - 3})`)
            .transition()
            .duration(1000)
            .attr("height", (d, i, c) => opts.ChartHeight - ScaleY(d.MULTI_CORE))
            .attr("y", d => ScaleY(d.MULTI_CORE));


            d3.select(`svg[id=${svgId}]`)
            .append('g')
            .selectAll('rect')
            .data(IntelData)
            .enter()
            .append('rect')
            .on('mouseover', mouseOverNavy)
            .on('mouseout', mouseOutBlue)
            .on("click", clickHandlerMulti)
            .attr("class", "bar-element")
            .attr("fill", 'blue')
            .attr("width", ScaleX.bandwidth() / 4)
            .attr("x", (d, i, c) => {
                const year = ScaleX((d as any).YEAR.toString()) || '';
                return year;
            })
            .attr("height", 0)
            .attr("y", opts.ChartHeight)
            .attr("transform", (d) => `translate(${ChartMargin.left * 2.5}, ${-ChartMargin.bottom - 3})`)
            .transition()
            .duration(1000)
            .attr("height", (d, i, c) => opts.ChartHeight - ScaleY(d.MULTI_CORE))
            .attr("y", d => ScaleY(d.MULTI_CORE));

        d3.select(`svg[id=${svgId}]`)
            .append('g')
            .attr('transform', `translate(${ChartMargin.left}, ${ChartMargin.bottom})`)
            .call(AxisY);

        d3.select(`svg[id=${svgId}]`)
            .append('g')
            .attr('transform', `translate(${ChartMargin.left - 4}, ${opts.ChartHeight - 3 - ChartMargin.bottom})`)
            .call(AxisX);
    }

    useEffect(() =>{
        initializeSvg(svgId, opts);
        drawSingleChart();
    }, [])

    return (
        <div className="chart-container">
            <div className="container">
                <div className="row justify-content-center">
                    <h3>Apple vs. Intel</h3>
                </div>
                <div className="row justify-content-center">
                    Comparisons of Geekbench scores between Apple A-series SoC and Intel Core i7 mobile CPUs
                </div>
                <div className="row">
                    <div className="col-sm">
                        <div className="row">
                            <button onClick={drawSingleChart} className="btn btn-primary">Single Core</button>
                        </div>
                        <div className="row">
                            <button onClick={drawMultiChart} className="btn btn-primary">Multi-Core</button>
                        </div>
                    </div>
                    <div className="col-lg" dangerouslySetInnerHTML={{__html: annotation}} />
                </div>
                <div className="row">
                    <div className="justify-content-start">
                        <svg id={svgId}></svg>
                    </div>
                </div>
                <div className="row">
                    <div className="attribution col-6 offset-md-9">
                        Data source: <a target="_blank" rel="noopener noreferrer" href="https://browser.geekbench.com/">Geekbench Browser</a>
                    </div>
                </div>
            </div>
        </div>
    );
}